(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const touchLike = window.matchMedia('(hover: none), (pointer: coarse)');
  const mobileViewport = window.matchMedia('(max-width: 767px)');
  const finePointer = window.matchMedia('(pointer: fine)');

  const MOBILE_ACTIVATION_DELAY = 1000;
  const MOBILE_SCROLL_SETTLE_DELAY = 120;

  const stopCard = (card, reset = false) => {
    const video = card?.querySelector('.gostosso-editorial-card__video');
    card?.classList.remove('is-media-active');

    if (!video) return;

    video.pause();

    if (reset) {
      try { video.currentTime = 0; } catch (_) {}
    }
  };

  const playCard = (card) => {
    if (!card || reduceMotion.matches || !card.matches('.has-secondary-media')) return;

    const secondary = card.querySelector('.gostosso-editorial-card__secondary');
    if (!secondary) return;

    card.classList.add('is-media-active');

    const video = card.querySelector('.gostosso-editorial-card__video');
    if (!video) return;

    video.muted = true;
    video.playsInline = true;
    video.play().catch(() => {});
  };

  const stopAllCards = (rail, reset = true) => {
    rail?.querySelectorAll('[data-gostosso-card]').forEach((card) => stopCard(card, reset));
  };

  const getCenteredCard = (rail) => {
    if (!rail || rail.offsetParent === null) return null;

    const cards = [...rail.querySelectorAll('[data-gostosso-card]')];
    if (!cards.length) return null;

    const railRect = rail.getBoundingClientRect();
    const railCenter = railRect.left + (railRect.width / 2);

    let centeredCard = null;
    let smallestDistance = Infinity;

    cards.forEach((card) => {
      const rect = card.getBoundingClientRect();

      if (rect.right <= railRect.left || rect.left >= railRect.right) return;

      const cardCenter = rect.left + (rect.width / 2);
      const distance = Math.abs(cardCenter - railCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        centeredCard = card;
      }
    });

    return centeredCard;
  };

  const clearMobileTimers = (rail) => {
    if (!rail) return;

    clearTimeout(rail._gostossoActivationTimer);
    clearTimeout(rail._gostossoSettleTimer);

    rail._gostossoActivationTimer = null;
    rail._gostossoSettleTimer = null;
  };

  const scheduleCenteredActivation = (rail) => {
    if (!rail || !mobileViewport.matches || !touchLike.matches || reduceMotion.matches) return;

    clearTimeout(rail._gostossoActivationTimer);

    rail._gostossoActivationTimer = setTimeout(() => {
      if (!mobileViewport.matches || !touchLike.matches || rail.offsetParent === null) return;

      const centeredCard = getCenteredCard(rail);
      if (!centeredCard) return;

      stopAllCards(rail, true);
      playCard(centeredCard);
    }, MOBILE_ACTIVATION_DELAY);
  };

  const resetForMobileMovement = (rail) => {
    if (!rail || !mobileViewport.matches || !touchLike.matches) return;

    clearMobileTimers(rail);
    stopAllCards(rail, true);
  };

  const scheduleAfterScrollSettles = (rail) => {
    if (!rail || !mobileViewport.matches || !touchLike.matches) return;

    clearTimeout(rail._gostossoSettleTimer);

    rail._gostossoSettleTimer = setTimeout(() => {
      scheduleCenteredActivation(rail);
    }, MOBILE_SCROLL_SETTLE_DELAY);
  };

  const updateControls = (rail) => {
    const panel = rail?.closest('[data-gostosso-panel]');
    if (!rail || !panel) return;

    const media = rail.querySelector('.gostosso-editorial-card__media');
    if (media) panel.style.setProperty('--gostosso-media-center', `${media.offsetTop + media.getBoundingClientRect().height / 2}px`);
    const previous = panel.querySelector('[data-gostosso-prev]');
    const next = panel.querySelector('[data-gostosso-next]');
    const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const hasOverflow = maxScroll > 4;

    panel.classList.toggle('has-overflow', hasOverflow);
    if (previous) previous.disabled = !hasOverflow || rail.scrollLeft <= 2;
    if (next) next.disabled = !hasOverflow || rail.scrollLeft >= maxScroll - 2;
  };

  const scrollOneCard = (rail, direction) => {
    const card = rail?.querySelector('[data-gostosso-card]');
    if (!rail || !card) return;

    const styles = getComputedStyle(rail);
    const gap = parseFloat(styles.columnGap || styles.gap) || 16;
    const amount = card.getBoundingClientRect().width + gap;

    rail.scrollBy({
      left: direction * amount,
      behavior: reduceMotion.matches ? 'auto' : 'smooth'
    });
  };

  const setupRail = (rail) => {
    if (!rail || rail.dataset.gostossoReady === 'true') return;
    rail.dataset.gostossoReady = 'true';

    const panel = rail.closest('[data-gostosso-panel]');
    const cards = [...rail.querySelectorAll('[data-gostosso-card]')];
    const previous = panel?.querySelector('[data-gostosso-prev]');
    const next = panel?.querySelector('[data-gostosso-next]');

    cards.forEach((card) => {
      if (!card.matches('.has-secondary-media')) return;

      card.addEventListener('mouseenter', () => {
        if (!touchLike.matches) playCard(card);
      });

      card.addEventListener('mouseleave', () => {
        if (!touchLike.matches) stopCard(card, true);
      });

      card.addEventListener('focusin', () => {
        if (!touchLike.matches) playCard(card);
      });

      card.addEventListener('focusout', () => {
        if (!touchLike.matches && !card.contains(document.activeElement)) {
          stopCard(card, true);
        }
      });
    });

    previous?.addEventListener('click', () => scrollOneCard(rail, -1));
    next?.addEventListener('click', () => scrollOneCard(rail, 1));

    rail.addEventListener('pointerdown', () => {
      resetForMobileMovement(rail);
    }, { passive: true });

    rail.addEventListener('touchstart', () => {
      resetForMobileMovement(rail);
    }, { passive: true });

    rail.addEventListener('scroll', () => {
      updateControls(rail);

      if (mobileViewport.matches && touchLike.matches) {
        resetForMobileMovement(rail);
        scheduleAfterScrollSettles(rail);
      }
    }, { passive: true });

    rail.addEventListener('pointerup', () => {
      if (mobileViewport.matches && touchLike.matches) {
        scheduleAfterScrollSettles(rail);
      }
    }, { passive: true });

    rail.addEventListener('touchend', () => {
      if (mobileViewport.matches && touchLike.matches) {
        scheduleAfterScrollSettles(rail);
      }
    }, { passive: true });

    if ('onscrollend' in window) {
      rail.addEventListener('scrollend', () => {
        if (mobileViewport.matches && touchLike.matches) {
          clearTimeout(rail._gostossoSettleTimer);
          scheduleCenteredActivation(rail);
        }
      }, { passive: true });
    }

    if (finePointer.matches) {
      rail.addEventListener('dragstart', (event) => event.preventDefault());
      let dragging = false;
      let startX = 0;
      let startScroll = 0;
      let suppressClick = false;

      rail.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;

        dragging = true;
        suppressClick = false;
        startX = event.clientX;
        startScroll = rail.scrollLeft;

      });

      rail.addEventListener('pointermove', (event) => {
        if (!dragging) return;

        const delta = event.clientX - startX;

        if (!suppressClick) {
          if (Math.abs(delta) <= 5) return;
          suppressClick = true;
          rail.classList.add('is-dragging');
          try { rail.setPointerCapture(event.pointerId); } catch (_) {}
        }

        rail.scrollLeft = startScroll - delta;

        if (suppressClick) event.preventDefault();
      });

      const endDrag = (event) => {
        if (!dragging) return;

        dragging = false;
        rail.classList.remove('is-dragging');

        try { rail.releasePointerCapture(event.pointerId); } catch (_) {}

        setTimeout(() => updateControls(rail), 0);
      };

      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);

      rail.addEventListener('click', (event) => {
        if (!suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);
    }

    requestAnimationFrame(() => {
      updateControls(rail);
      stopAllCards(rail, true);
      scheduleCenteredActivation(rail);
    });

    setTimeout(() => {
      updateControls(rail);
    }, 120);
  };

  const activateTab = (root, handle) => {
    root.querySelectorAll('[data-gostosso-tab]').forEach((tab) => {
      const active = tab.dataset.gostossoTab === handle;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    root.querySelectorAll('[data-gostosso-panel]').forEach((panel) => {
      const active = panel.dataset.gostossoPanel === handle;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);

      const rail = panel.querySelector('[data-gostosso-rail]');

      if (rail) {
        clearMobileTimers(rail);
        stopAllCards(rail, true);
      }

      if (active && rail) {
        setupRail(rail);
        rail.scrollTo({ left: 0, behavior: 'auto' });

        requestAnimationFrame(() => {
          updateControls(rail);
          scheduleCenteredActivation(rail);
        });
      }
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('.gostosso-editorial-hero').forEach((root) => {
      root.querySelectorAll('[data-gostosso-rail]').forEach(setupRail);

      root.querySelectorAll('[data-gostosso-tab]').forEach((tab) => {
        if (tab.dataset.gostossoTabReady === 'true') return;

        tab.dataset.gostossoTabReady = 'true';
        tab.addEventListener('click', () => activateTab(root, tab.dataset.gostossoTab));
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      document.querySelectorAll('[data-gostosso-rail]').forEach((rail) => {
        clearMobileTimers(rail);
        stopAllCards(rail, true);
      });
    }
  });

  mobileViewport.addEventListener?.('change', () => {
    document.querySelectorAll('[data-gostosso-rail]').forEach((rail) => {
      clearMobileTimers(rail);
      stopAllCards(rail, true);

      if (mobileViewport.matches && touchLike.matches) {
        scheduleCenteredActivation(rail);
      }
    });
  });

  let resizeTimer;

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {
      document.querySelectorAll('[data-gostosso-rail]').forEach((rail) => {
        updateControls(rail);

        if (mobileViewport.matches && touchLike.matches) {
          resetForMobileMovement(rail);
          scheduleAfterScrollSettles(rail);
        }
      });
    }, 150);
  });
})();
